import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SingleValue } from 'react-select';

import { ColumnDef } from '@tanstack/react-table';

import { Table } from '@/common';
import { Spinner, Tag } from '@/components/generic';
import { FilterPopUpValues } from '@/components/layouts';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { PAGE_SIZE } from '@/constants';
import { PERMISSION_TYPE, PRIVATE_PAGE_ROUTES } from '@/enums';
import { OrgPopup } from '@/helpers';
import i18n from '@/i18n';
import { FilterThemes } from '@/pages/Themes/FilterThemes';
import { PremiumIcon } from '@/resources';
import { useDeleteVideoThemeMutation, useGetAllVideoTheme } from '@/services';
import { AllowedPermissions, SelectOptionProps, TableData, Theme } from '@/types';

export const Themes = () => {
  const [t] = useTranslation('common');
  const [search, setSearch] = useState<string>();
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);
  const [selectedTheme, setSelectedTheme] = useState<Theme>();
  const [isOpenFilterModal, setIsOpenFilterModal] = useState<boolean>(false);
  const [appliedFiltersCounter, setAppliedFiltersCounter] = useState<number>(0);
  const [filters, setFilters] = useState<{ type: SingleValue<SelectOptionProps> }>({
    type: null
  });

  const navigate = useNavigate();

  const permissions: AllowedPermissions = {
    createPermission: PERMISSION_TYPE.CREATE_VIDEO_THEME,
    editPermission: PERMISSION_TYPE.UPDATE_VIDEO_THEME,
    deletePermission: PERMISSION_TYPE.DELETE_VIDEO_THEME,
    viewPermission: PERMISSION_TYPE.VIEW_THEMES
  };

  const {
    isLoading,
    data: allThemes,
    isFetchingNextPage,
    refetch,
    fetchNextPage,
    hasNextPage
  } = useGetAllVideoTheme(PAGE_SIZE, search, undefined, filters.type?.value as number);

  const { mutate: deleteTheme } = useDeleteVideoThemeMutation(queryClient, {
    onSuccess: () => {
      setIsOpenDeleteModal(false);
      refetch();
    },
    onError: () => {
      setIsOpenDeleteModal(false);
    }
  });

  const formatData = (): TableData[] => {
    return allThemes?.pages.flatMap((page) =>
      page.data?.items.map((themeData: Theme) => ({
        ...themeData,
        enabled: true
      }))
    ) as TableData[];
  };

  const getThemeColumns: () => ColumnDef<TableData>[] = () => [
    {
      id: 'name',
      accessorKey: 'name',
      header: i18n.t('name', { ns: 'common' }),
      cell: ({ row }) => {
        const theme = row.original as unknown as Theme;
        return (
          <div className='flex items-center gap-2'>
            {theme.type === 1 && <PremiumIcon className='size-5' />}
            <span>{theme.name}</span>
          </div>
        );
      }
    },
    {
      id: 'description',
      accessorKey: 'description',
      header: i18n.t('description', { ns: 'common' }),
      cell: ({ getValue }) => {
        const value = getValue() as string;
        return (
          <div className='truncate md:max-w-[250px] lg:max-w-[350px]' title={value}>
            {value}
          </div>
        );
      }
    },
    {
      id: 'organizations',
      header: i18n.t('organizations', { ns: 'common' }),
      accessorKey: 'organizations',
      cell: (props) => {
        const orgs = (props.row.original as unknown as Theme)?.organizations ?? [];
        const [showPopup, setShowPopup] = useState(false);
        const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
        const extraRef = useRef<HTMLDivElement>(null);
        if (!orgs.length) {
          return null;
        }
        const firstOrg = orgs[0]?.name;
        const extraCount = orgs.length - 1;
        const handleMouseEnter = () => {
          if (extraRef.current) {
            setAnchorRect(extraRef.current.getBoundingClientRect());
          }
          setShowPopup(true);
        };
        const handleMouseLeave = () => {
          setShowPopup(false);
        };
        return (
          <div className='relative flex items-center'>
            <span className='text-sm'>{firstOrg}</span>
            {extraCount > 0 && (
              <div
                ref={extraRef}
                className='ml-2 cursor-pointer rounded-lg bg-primary-gray px-2 text-xs text-primary-blue'
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{ position: 'relative', zIndex: 1 }}>
                +{extraCount}
                {showPopup && (
                  <OrgPopup
                    orgs={orgs.slice(1)}
                    anchorRect={anchorRect}
                    onClose={handleMouseLeave}
                  />
                )}
              </div>
            )}
          </div>
        );
      }
    },
    {
      id: 'isPublish',
      header: i18n.t('status', { ns: 'common' }),
      accessorKey: 'isPublish',
      cell: (props) => <Tag value={props.getValue() ? 'Published' : 'Drafted'} />
    }
  ];

  const searchCallback = (query: string) => {
    setSearch(query);
  };

  const handleCreateTheme = () => {
    navigate(PRIVATE_PAGE_ROUTES.CREATE_THEME);
  };

  const handleEditTheme = (theme: Theme) => {
    const editThemeUrl = PRIVATE_PAGE_ROUTES.EDIT_THEME.replace(':themeId', theme.id.toString());
    navigate(editThemeUrl);
  };

  const handleDeleteTheme = (row: Theme) => {
    setIsOpenDeleteModal(true);
    setSelectedTheme(row);
  };

  const handleViewDetails = (row: Theme) => {
    navigate(`${PRIVATE_PAGE_ROUTES.THEMES}/${row.id}`);
  };

  const handleCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
    setSelectedTheme(undefined);
  };

  const handleFilterModalVisibility = () => {
    setIsOpenFilterModal(!isOpenFilterModal);
  };

  const resetFilters = () => {
    setIsOpenFilterModal(false);
    setFilters({ type: null });
    setAppliedFiltersCounter(0);
  };

  const applyFilters = (values: { filterByType: SingleValue<SelectOptionProps> }) => {
    setIsOpenFilterModal(false);
    setFilters({ type: values.filterByType });
  };

  useEffect(() => {
    const numberOfFilters = Object.values(filters).filter((value) => value !== null);
    setAppliedFiltersCounter(numberOfFilters.length);
  }, [filters]);

  return (
    <div className='flex size-full flex-col items-center'>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className='flex h-screen w-full flex-col px-3 pb-3'>
          {allThemes && (
            <Table
              data={formatData()}
              columns={getThemeColumns()}
              tableTitle={t('themes')}
              addButtonLabel={t('addTheme')}
              permissions={permissions}
              search={searchCallback}
              handleCreate={handleCreateTheme}
              handleEdit={(item) => handleEditTheme(item as unknown as Theme)}
              handleDelete={(item) => handleDeleteTheme(item as unknown as Theme)}
              handleViewDetails={(item) => handleViewDetails(item as unknown as Theme)}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              hiddenColumnsMobile={['organizations', 'description', 'isPublish']}
              mobileColumnOffset={3}
              handleOpenFilterModal={handleFilterModalVisibility}
              numberOfAppliedFilters={appliedFiltersCounter}
            />
          )}
          {selectedTheme && (
            <ConfirmationModal
              isOpen={isOpenDeleteModal}
              message={t('deleteMessage', {
                item: `${selectedTheme.name}`
              })}
              onClose={handleCloseDeleteModal}
              onConfirm={() => {
                selectedTheme && selectedTheme.id && deleteTheme(selectedTheme.id);
              }}
            />
          )}
          {isOpenFilterModal && (
            <FilterThemes
              apply={applyFilters as (values: FilterPopUpValues) => void}
              reset={resetFilters}
              onClose={() => setIsOpenFilterModal(false)}
              filterByType={filters.type}
            />
          )}
        </div>
      )}
    </div>
  );
};
